import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CanvasApp from "./CanvasApp";
import { useAuthFetch } from "../../../utils/useAuthFetch";
jest.mock("../../../utils/useAuthFetch", () => ({ useAuthFetch: jest.fn() }));

const mockUseCanvas = jest.fn();
const mockUseCanvasData = jest.fn();
const mockUseCanvasSubmit = jest.fn();
const mockUseViewRow = jest.fn();
const mockEmitWorkspaceObjectChange = jest.fn();
const testQueryClient = new QueryClient();
const queryClientWrapper = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={testQueryClient}>{children}</QueryClientProvider>
);
let mockWorkspaceRole: "owner" | "admin" | "editor" | "user" = "user";
let mockOnSelectionChange:
  ((event: Record<string, unknown>) => void) | undefined;
let mockRendererMounts = 0;
let mockRuntimeRowData: Record<string, unknown> | undefined;
let mockRuntime: any;
const mockRelatedQuery = jest.fn();
jest.mock("../hooks/useRelatedRecords", () => ({
  useRelatedRecords: (...args: unknown[]) => mockRelatedQuery(...args),
}));
jest.mock("../hooks/useViewDataQuery", () => ({
  useWorkspaceObjectChangeQueryInvalidation: jest.fn(),
  useViewQuery: () => ({
    data: {
      id: "jobs",
      focus_table_id: "table-registrations",
      fields: [],
      joins: [],
    },
    refetch: jest.fn().mockResolvedValue({
      data: { focus_table_id: "table-registrations" },
    }),
  }),
  useCreateRowMutation: () => ({ mutateAsync: jest.fn() }),
  useUpdateRowMutation: () => ({ mutateAsync: jest.fn() }),
}));
jest.mock("../services/workspaceObjectChanges", () => ({
  ...jest.requireActual("../services/workspaceObjectChanges"),
  emitWorkspaceObjectChange: (...args: unknown[]) =>
    mockEmitWorkspaceObjectChange(...args),
}));
jest.mock("../components/viewGrid", () => ({
  ViewGridNew: () => <div>Related rows</div>,
}));
jest.mock("../components/MyVaultAgent", () => {
  const React = require("react");
  return {
    FloatingChatWidget: (props: Record<string, unknown>) =>
      React.createElement("div", {
        "data-testid": "canvas-assistant",
        "data-canvas-id": props.canvasId,
        "data-allow-canvas-runtime": String(props.allowCanvasRuntime),
      }),
  };
});
let mockRuntimePrimaryRowId: string | null | undefined;

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({
    workspace: { id: "workspace-1" },
    isEditor: mockWorkspaceRole !== "user",
  }),
}));

jest.mock("../hooks/useCanvases", () => ({
  useCanvas: (...args: unknown[]) => mockUseCanvas(...args),
  useCanvasData: (...args: unknown[]) => mockUseCanvasData(...args),
  useCanvasSubmit: (...args: unknown[]) => mockUseCanvasSubmit(...args),
  useViewRow: (...args: unknown[]) => mockUseViewRow(...args),
}));

function MockCanvasRenderer({
  elements,
  fieldState,
  footer,
  onActivePanelChange,
  renderDataWidget,
}: {
  elements: Array<{
    id?: string;
    type: string;
    props: { readonly?: boolean };
    data_binding?: { field_path?: string };
  }>;
  fieldState: Record<
    string,
    { value?: unknown; onChange?: (value: unknown) => void }
  >;
  renderDataWidget?: (element: any) => React.ReactNode;
  footer?: React.ReactNode;
  onActivePanelChange?: (containerId: string, panelId: string) => void;
}) {
  // Like the real TabsContainer: the shown tab is local state that starts at the
  // first panel and is reported from an effect, so a remount resets it.
  // A field's state, found the way the real renderer finds it: through the element's own
  // binding key (the card's keys are not the plain field names).
  const stateOf = (id: string, plainKey: string) => {
    const key = elements.find((element) => element.id === id)?.data_binding
      ?.field_path;
    const byPlainKey = Object.keys(fieldState).find(
      (name) => name === plainKey || name.endsWith(`:${plainKey}`),
    );
    return (
      (key ? fieldState[key] : undefined) ??
      (byPlainKey ? fieldState[byPlainKey] : undefined)
    );
  };
  const [shownTab, setShownTab] = React.useState("panel-a");
  React.useEffect(() => {
    mockRendererMounts += 1;
  }, []);
  React.useEffect(() => {
    onActivePanelChange?.("tabs", shownTab);
  }, [onActivePanelChange, shownTab]);
  return (
    <div data-testid="canvas-renderer">
      <button onClick={() => setShownTab("panel-a")}>Show tab A</button>
      <button onClick={() => setShownTab("panel-b")}>Show tab B</button>
      <button onClick={() => setShownTab("panel-report")}>
        Show report tab
      </button>
      <div data-testid="shown-tab">{shownTab}</div>
      <button onClick={() => stateOf("field-b", "name")?.onChange?.("Bob")}>
        Edit name
      </button>
      <button
        onClick={() =>
          mockOnSelectionChange?.({
            type: "row_selected",
            elementId: "grid-1",
            rowId: "row-1",
          })
        }
      >
        Select first
      </button>
      <button
        onClick={() =>
          mockOnSelectionChange?.({
            type: "row_selected",
            elementId: "grid-1",
            rowId: "row-2",
          })
        }
      >
        Select second
      </button>
      <button
        onClick={() =>
          mockOnSelectionChange?.({
            type: "selection_cleared",
            elementId: "grid-1",
          })
        }
      >
        Clear selection
      </button>
      <div data-testid="name-value">
        {String(stateOf("field-b", "name")?.value ?? "")}
      </div>
      <div data-testid="detail-title">
        {String(stateOf("field-a", "title")?.value ?? "")}
      </div>
      <div data-testid="tab-b-title">
        {String(stateOf("field-b2", "__none__")?.value ?? "")}
      </div>
      <div data-testid="field-readonly">
        {String(
          elements.find((element) => element.type === "field")?.props
            .readonly ?? false,
        )}
      </div>
      <button
        onClick={() => stateOf("field-a", "title")?.onChange?.("Draft title")}
      >
        Edit title
      </button>
      {elements
        .filter((element) => element.type === "related_records")
        .map((element) => (
          <React.Fragment key={element.id}>
            {renderDataWidget?.(element)}
          </React.Fragment>
        ))}
      {footer}
    </div>
  );
}

jest.mock("../components/canvasBuilder", () => ({
  CanvasRenderer: MockCanvasRenderer,
  CanvasDataWidgetRenderer: jest.requireActual(
    "../components/canvasBuilder/elements/CanvasDataWidgetRenderer",
  ).CanvasDataWidgetRenderer,
  CanvasViewGridRenderer: () => (
    <div data-testid="view-grid-renderer">grid</div>
  ),
  EmbeddedCanvasRenderer: () => (
    <div data-testid="embedded-canvas-renderer">embedded</div>
  ),
}));

jest.mock("../components/canvasBuilder/CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => mockRuntime,
  CanvasRuntimeProvider: ({
    value,
    children,
  }: {
    value: {
      onSelectionChange?: typeof mockOnSelectionChange;
      rowData?: Record<string, unknown>;
      primaryRowId?: string | null;
    };
    children: React.ReactNode;
  }) => {
    mockRuntime = { ...value, interactionBus: { getSelectedRow: () => null } };
    mockOnSelectionChange = value.onSelectionChange;
    mockRuntimeRowData = value.rowData;
    mockRuntimePrimaryRowId = value.primaryRowId;
    return children;
  },
}));

jest.mock("../theme/CanvasThemeScope", () => ({
  CanvasThemeScope: ({ children }: { children: React.ReactNode }) => children,
}));

const renderCanvasApp = (
  query = "",
  wrapper?: React.ComponentType<{ children: React.ReactNode }>,
) =>
  render(
    <MemoryRouter
      initialEntries={[
        `/workspace/workspace-1/myvault/canvases/canvas-1${query}`,
      ]}
    >
      <Routes>
        <Route
          path="/workspace/:workspaceId/myvault/canvases/:id"
          element={<CanvasApp />}
        />
      </Routes>
    </MemoryRouter>,
    { wrapper },
  );

describe("CanvasApp related primary mapping", () => {
  it("queries the actual primary source when it is second, without leaking its row to the first source", () => {
    mockRelatedQuery.mockReturnValue({
      data: null,
      page: 1,
      setPage: jest.fn(),
      loading: false,
    });
    const widget = (id: string, alias: string) => ({
      id,
      element_type: "WIDGET",
      grid_column: 1,
      grid_row: 1,
      column_span: 12,
      row_span: 4,
      config_json: {
        widget_type: "RELATED_RECORDS",
        data_source_alias: alias,
        related_records_config: {
          related_data_source_alias: "jobs",
          relationship_path: "jobs",
          display_fields: ["title"],
        },
      },
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Related",
        data_sources: [
          { alias: "secondary", view_id: "other", is_primary: false },
          { alias: "people", view_id: "employees", is_primary: true },
          { alias: "jobs", view_id: "jobs", is_primary: false },
        ],
        elements: [
          widget("primary-widget", "people"),
          widget("secondary-widget", "secondary"),
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: {
        record_id: "me",
        record: { name: "Johan" },
        records: [],
        record_count: 1,
      },
      primaryData: { record_id: "me", record: { name: "Johan" } },
      loading: false,
    });
    mockUseCanvasSubmit.mockReturnValue({ submit: jest.fn(), loading: false });
    renderCanvasApp();
    expect(mockRelatedQuery).toHaveBeenCalledWith(
      "canvas-1",
      "primary-widget",
      "me",
    );
    expect(mockRelatedQuery).toHaveBeenCalledWith(
      "canvas-1",
      "secondary-widget",
      null,
    );
  });
});

describe("CanvasApp", () => {
  beforeEach(() => {
    mockEmitWorkspaceObjectChange.mockReset();
    mockWorkspaceRole = "user";
    mockOnSelectionChange = undefined;
    mockRendererMounts = 0;
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Browse Canvas",
        description: null,
        archetype: "browse",
        data_sources: [],
        elements: [
          {
            id: "el-1",
            element_type: "TEXT",
            grid_column: 1,
            grid_row: 1,
            column_span: 12,
            row_span: 1,
            config_json: { text: "Hello" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: {
        record: null,
        records: [],
        record_count: 0,
        can_edit: false,
        can_create: false,
        can_delete: false,
      },
      loading: false,
      error: null,
    });
    mockUseCanvasSubmit.mockReturnValue({
      submit: jest.fn(),
      loading: false,
      error: null,
      result: null,
    });
    mockUseViewRow.mockReturnValue({
      data: null,
      loading: false,
      error: null,
      refresh: jest.fn(),
    });
  });

  it.each([true, false])(
    "mounts the canvas assistant only when the full response allows it (%s)",
    (available) => {
      mockUseCanvas.mockReturnValue({
        canvas: {
          id: "canvas-1",
          name: "Browse Canvas",
          description: null,
          archetype: "browse",
          canvas_assistant_available: available,
          data_sources: [],
          elements: [
            {
              id: "el-1",
              element_type: "TEXT",
              grid_column: 1,
              grid_row: 1,
              column_span: 12,
              row_span: 1,
              config_json: { text: "Hello" },
            },
          ],
        },
        loading: false,
        error: null,
      });

      renderCanvasApp();

      const assistant = screen.queryByTestId("canvas-assistant");
      expect(assistant !== null).toBe(available);
      expect(assistant?.getAttribute("data-canvas-id") ?? null).toBe(
        available ? "canvas-1" : null,
      );
      expect(assistant?.getAttribute("data-allow-canvas-runtime") ?? null).toBe(
        available ? "true" : null,
      );
    },
  );

  it.each(["owner", "admin", "editor", "user"] as const)(
    "shows canvas authoring only to %s",
    (role) => {
      mockWorkspaceRole = role;
      renderCanvasApp();
      expect(
        Boolean(screen.queryByRole("button", { name: "Edit Canvas" })),
      ).toBe(role !== "user");
    },
  );

  it.each([
    ["read", false],
    ["read-write", true],
  ] as const)(
    "shows a submit control only for a %s field with create authority",
    (effectiveAccess, canSubmit) => {
      mockUseCanvas.mockReturnValue({
        canvas: {
          id: "canvas-1",
          name: "Entry",
          data_sources: [
            { alias: "main", view_id: "view-1", is_primary: true },
          ],
          elements: [
            {
              id: "title-field",
              element_type: "FIELD",
              effective_access: effectiveAccess,
              data_binding: {
                data_source_alias: "main",
                field_path: "title",
              },
              config_json: { label: "Title" },
            },
          ],
        },
        loading: false,
        error: null,
      });
      mockUseCanvasData.mockReturnValue({
        data: { record: null, records: [], can_create: true, can_edit: false },
        loading: false,
        error: null,
      });

      renderCanvasApp();
      expect(screen.getByTestId("field-readonly")).toHaveTextContent(
        String(!canSubmit),
      );
      expect(Boolean(screen.queryByRole("button", { name: "Submit" }))).toBe(
        canSubmit,
      );
    },
  );

  it("submits a form through its writable alias when the primary source is read-only", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Event Manager",
        data_sources: [
          {
            alias: "attendee_metrics",
            view_id: "joined-view",
            is_primary: true,
          },
          { alias: "registration_form", view_id: "registration-view" },
        ],
        elements: [
          {
            id: "metrics-grid",
            element_type: "VIEW_GRID",
            config_json: { data_source_alias: "attendee_metrics" },
          },
          {
            id: "field-b",
            element_type: "FIELD",
            effective_access: "read-write",
            data_binding: {
              data_source_alias: "registration_form",
              field_path: "name",
            },
            config_json: { label: "Attendee name" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: { record: null, records: [], can_create: true, can_edit: false },
      loading: false,
      error: null,
    });

    renderCanvasApp();
    expect(mockUseCanvasData).toHaveBeenCalledWith(
      "workspace-1",
      "canvas-1",
      undefined,
      "registration_form",
    );
    fireEvent.click(screen.getByRole("button", { name: "Edit name" }));
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));

    await waitFor(() => expect(submit).toHaveBeenCalledTimes(1));
    expect(submit).toHaveBeenCalledWith(
      "canvas-1",
      { data_source_alias: "registration_form", data: { name: "Bob" } },
      undefined,
    );
  });

  it("keeps a dashboard canvas visible, resets its form, and emits the changed table after submit", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Event Manager",
        settings: { success_message: "Attendee saved" },
        data_sources: [
          {
            alias: "attendee_metrics",
            view_id: "joined-view",
            is_primary: true,
          },
          { alias: "registration_form", view_id: "registration-view" },
        ],
        elements: [
          {
            id: "attendee-grid",
            element_type: "VIEW_GRID",
            config_json: { data_source_alias: "attendee_metrics" },
          },
          {
            id: "attendee-field",
            element_type: "FIELD",
            effective_access: "read-write",
            data_binding: {
              data_source_alias: "registration_form",
              field_path: "name",
            },
            config_json: { label: "Attendee name" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: { record: null, records: [], can_create: true, can_edit: false },
      loading: false,
      error: null,
    });

    renderCanvasApp();
    fireEvent.click(screen.getByRole("button", { name: "Edit name" }));
    expect(screen.getByTestId("name-value")).toHaveTextContent("Bob");
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));

    expect(await screen.findByText("Attendee saved")).toBeInTheDocument();
    expect(screen.getByTestId("canvas-renderer")).toBeInTheDocument();
    expect(screen.getByTestId("name-value")).toBeEmptyDOMElement();
    expect(screen.queryByText("Success!")).not.toBeInTheDocument();
    expect(mockEmitWorkspaceObjectChange).toHaveBeenCalledWith({
      workspaceId: "workspace-1",
      changedObjects: [
        {
          type: "table",
          id: "table-registrations",
          action: "updated",
        },
      ],
    });
    expect(submit).toHaveBeenCalledWith(
      "canvas-1",
      { data_source_alias: "registration_form", data: { name: "Bob" } },
      undefined,
    );
  });

  it("keeps the existing success screen for a pure entry form", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: { success: true },
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Entry",
        data_sources: [
          { alias: "main", view_id: "registrations", is_primary: true },
        ],
        elements: [
          {
            id: "attendee-field",
            element_type: "FIELD",
            effective_access: "read-write",
            data_binding: { data_source_alias: "main", field_path: "name" },
            config_json: { label: "Attendee name" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: { record: null, records: [], can_create: true, can_edit: false },
      loading: false,
      error: null,
    });

    renderCanvasApp();
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));

    await waitFor(() => expect(submit).toHaveBeenCalledTimes(1));
    expect(await screen.findByText("Success!")).toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: "Submit Another" }),
    ).toBeInTheDocument();
    expect(screen.queryByTestId("canvas-renderer")).not.toBeInTheDocument();
    expect(mockEmitWorkspaceObjectChange).toHaveBeenCalledWith({
      workspaceId: "workspace-1",
      changedObjects: [
        { type: "table", id: "table-registrations", action: "updated" },
      ],
    });
  });

  it("rejects form fields bound to more than one source", async () => {
    const submit = jest.fn();
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Mixed form",
        data_sources: [
          { alias: "first", view_id: "view-1", is_primary: true },
          { alias: "second", view_id: "view-2" },
        ],
        elements: [
          {
            id: "field-b",
            element_type: "FIELD",
            effective_access: "read-write",
            data_binding: { data_source_alias: "first", field_path: "name" },
            config_json: { label: "Name" },
          },
          {
            id: "field-a",
            element_type: "FIELD",
            effective_access: "read-write",
            data_binding: { data_source_alias: "second", field_path: "email" },
            config_json: { label: "Email" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: { record: null, records: [], can_create: true, can_edit: false },
      loading: false,
      error: null,
    });

    renderCanvasApp();
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));

    expect(
      await screen.findByText(
        "This form has fields bound to multiple data sources. Bind all submitted fields to one writable view.",
      ),
    ).toBeInTheDocument();
    expect(submit).not.toHaveBeenCalled();
  });

  it.each([
    [{ readonly: true }, false],
    [{ is_readonly: true }, false],
    [{ readOnly: true }, false],
    [{ displayMode: "text" }, false],
    [{ displayMode: "readOnly" }, false],
    [{ readOnly: false }, true],
    [{ displayMode: "input" }, true],
    [{}, true],
  ] as const)(
    "a field configured %j counts as %s for the form footer",
    (config, canSubmit) => {
      mockUseCanvas.mockReturnValue({
        canvas: {
          id: "canvas-1",
          name: "Entry",
          data_sources: [
            { alias: "main", view_id: "view-1", is_primary: true },
          ],
          elements: [
            {
              id: "title-field",
              element_type: "FIELD",
              effective_access: "read-write",
              data_binding: { data_source_alias: "main", field_path: "title" },
              config_json: { label: "Title", ...config },
            },
          ],
        },
        loading: false,
        error: null,
      });
      mockUseCanvasData.mockReturnValue({
        data: { record: null, records: [], can_create: true, can_edit: false },
        loading: false,
        error: null,
      });

      renderCanvasApp();
      expect(Boolean(screen.queryByRole("button", { name: "Submit" }))).toBe(
        canSubmit,
      );
      expect(Boolean(screen.queryByRole("button", { name: "Cancel" }))).toBe(
        canSubmit,
      );
      // The renderer is told the field is read-only whenever the footer is not offered.
      expect(screen.getByTestId("field-readonly")).toHaveTextContent(
        String(!canSubmit),
      );
    },
  );
  it("shows a clear unavailable state for a plain user's unpublished canvas link", () => {
    mockWorkspaceRole = "user";
    mockUseCanvas.mockReturnValue({
      canvas: null,
      loading: false,
      error: "HTTP Error: status: 404",
      errorStatus: 404,
    });

    renderCanvasApp();
    expect(
      screen.getByText("Canvas not found or not available."),
    ).toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: "Back to Apps" }),
    ).toBeInTheDocument();
    expect(screen.queryByTestId("canvas-renderer")).not.toBeInTheDocument();
    expect(
      screen.queryByText("HTTP Error: status: 404"),
    ).not.toBeInTheDocument();
  });

  it("shows an explicit empty-data state for browse canvases with no records", () => {
    renderCanvasApp();

    expect(screen.getByText("No records yet.")).toBeInTheDocument();
    expect(
      screen.getByText(
        "This canvas is ready, but its primary data source has not produced any records.",
      ),
    ).toBeInTheDocument();
  });

  it("loads selected records and emits a table change when saving the active row", async () => {
    let secondRowPending = false;
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Positions",
        data_sources: [{ alias: "main", view_id: "view-1", is_primary: true }],
        elements: [
          {
            id: "grid-1",
            element_type: "VIEW_GRID",
            config_json: { data_source_alias: "main" },
          },
          {
            id: "panel-1",
            element_type: "CONTAINER",
            config_json: {
              container_type: "SECTION",
              selection_binding: {
                source_grid_element_id: "grid-1",
                data_source_alias: "main",
                mode: "edit",
                empty_state_text: "Select a position",
              },
            },
          },
          {
            id: "field-1",
            element_type: "FIELD",
            parent_element_id: "panel-1",
            data_binding: { data_source_alias: "main", field_path: "title" },
            config_json: { label: "Title" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: { record: null, records: [], record_count: 0, can_edit: true },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string) => ({
        data: {
          record_id:
            rowId === "row-2" && secondRowPending ? "row-1" : rowId || null,
          record: rowId
            ? {
                id: rowId === "row-2" && secondRowPending ? "row-1" : rowId,
                title:
                  rowId === "row-1" || secondRowPending ? "First" : "Second",
              }
            : null,
          can_edit: true,
        },
        loading: rowId === "row-2" && secondRowPending,
        error: null,
        refresh: jest.fn(),
      }),
    );

    renderCanvasApp();
    expect(
      screen.queryByRole("button", { name: "Save" }),
    ).not.toBeInTheDocument();
    fireEvent.click(screen.getByText("Select first"));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent("First"),
    );
    fireEvent.click(screen.getByText("Edit title"));
    expect(screen.getByTestId("detail-title")).toHaveTextContent("Draft title");
    fireEvent.click(screen.getByRole("button", { name: "Cancel" }));
    expect(screen.getByTestId("detail-title")).toHaveTextContent("First");
    secondRowPending = true;
    fireEvent.click(screen.getByText("Select second"));
    expect(screen.getByTestId("detail-title")).toBeEmptyDOMElement();
    expect(screen.getByRole("button", { name: "Save" })).toBeDisabled();
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    expect(submit).not.toHaveBeenCalled();
    secondRowPending = false;
    fireEvent.click(screen.getByText("Select first"));
    fireEvent.click(screen.getByText("Select second"));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent("Second"),
    );
    fireEvent.click(screen.getByText("Edit title"));
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    await waitFor(() =>
      expect(submit).toHaveBeenCalledWith(
        "canvas-1",
        expect.objectContaining({
          data_source_alias: "main",
          data: { title: "Draft title" },
        }),
        "row-2",
      ),
    );
    expect(submit).toHaveBeenCalledTimes(1);
    expect(mockEmitWorkspaceObjectChange).toHaveBeenCalledWith({
      workspaceId: "workspace-1",
      changedObjects: [
        { type: "table", id: "table-registrations", action: "updated" },
      ],
    });
    expect(mockUseCanvasData).toHaveBeenCalledWith(
      "workspace-1",
      "canvas-1",
      "row-2",
      "main",
    );
    fireEvent.click(screen.getByText("Clear selection"));
    expect(
      screen.queryByRole("button", { name: "Save" }),
    ).not.toBeInTheDocument();
  });

  it.each([
    {
      name: "has no mode (the API default is read)",
      mode: undefined,
      readOnly: true,
      saveShown: false,
    },
    { name: "is in edit mode", mode: "edit", readOnly: false, saveShown: true },
  ])(
    "a selection card that $name: field read-only=$readOnly, Save shown=$saveShown",
    async ({ mode, readOnly, saveShown }) => {
      mockUseCanvas.mockReturnValue({
        canvas: {
          id: "canvas-1",
          name: "People",
          data_sources: [
            { alias: "main", view_id: "view-1", is_primary: true },
          ],
          elements: [
            {
              id: "grid-1",
              element_type: "VIEW_GRID",
              config_json: { data_source_alias: "main" },
            },
            {
              id: "panel-1",
              element_type: "CONTAINER",
              config_json: {
                container_type: "SECTION",
                selection_binding: {
                  source_grid_element_id: "grid-1",
                  data_source_alias: "main",
                  ...(mode ? { mode } : {}),
                },
              },
            },
            {
              id: "field-1",
              element_type: "FIELD",
              parent_element_id: "panel-1",
              data_binding: { data_source_alias: "main", field_path: "title" },
              config_json: { label: "Title" },
            },
          ],
        },
        loading: false,
        error: null,
      });
      mockUseCanvasData.mockImplementation(
        (_workspace: string, _canvas: string, rowId?: string) => ({
          data: {
            record_id: rowId || null,
            record: rowId ? { id: rowId, title: "Ada" } : null,
            can_edit: true,
          },
          loading: false,
          error: null,
          refresh: jest.fn(),
        }),
      );
      renderCanvasApp();
      fireEvent.click(screen.getByText("Select first"));
      await waitFor(() =>
        expect(screen.getByTestId("detail-title")).toHaveTextContent("Ada"),
      );
      expect(screen.getByTestId("field-readonly")).toHaveTextContent(
        String(readOnly),
      );
      expect(Boolean(screen.queryByRole("button", { name: "Save" }))).toBe(
        saveShown,
      );
    },
  );

  it("keeps a read-only detail panel non-editable even when the actor can edit", async () => {
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "People",
        data_sources: [{ alias: "main", view_id: "view-1", is_primary: true }],
        elements: [
          {
            id: "grid-1",
            element_type: "VIEW_GRID",
            config_json: { data_source_alias: "main" },
          },
          {
            id: "panel-1",
            element_type: "CONTAINER",
            config_json: {
              container_type: "SECTION",
              selection_binding: {
                source_grid_element_id: "grid-1",
                data_source_alias: "main",
                mode: "read",
                empty_state_text: "Select a person",
              },
            },
          },
          {
            id: "field-1",
            element_type: "FIELD",
            parent_element_id: "panel-1",
            data_binding: { data_source_alias: "main", field_path: "title" },
            config_json: { label: "Title" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: { record: null, can_edit: true },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string) => ({
        data: {
          record_id: rowId || null,
          record: rowId ? { id: rowId, title: "Ada" } : null,
          can_edit: true,
        },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );
    renderCanvasApp();
    fireEvent.click(screen.getByText("Select first"));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent("Ada"),
    );
    expect(screen.getByTestId("field-readonly")).toHaveTextContent("true");
    expect(
      screen.queryByRole("button", { name: "Save" }),
    ).not.toBeInTheDocument();
  });

  it("keeps a non-primary selection alias for detail load, cancel, and save", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "People",
        data_sources: [
          { alias: "main", view_id: "view-1", is_primary: true },
          { alias: "other", view_id: "view-2", is_primary: false },
        ],
        elements: [
          {
            id: "grid-1",
            element_type: "VIEW_GRID",
            config_json: { data_source_alias: "other" },
          },
          {
            id: "panel-1",
            element_type: "CONTAINER",
            config_json: {
              container_type: "SECTION",
              selection_binding: {
                source_grid_element_id: "grid-1",
                data_source_alias: "other",
                mode: "edit",
              },
            },
          },
          {
            id: "field-1",
            element_type: "FIELD",
            parent_element_id: "panel-1",
            data_binding: { data_source_alias: "other", field_path: "title" },
            config_json: { label: "Title" },
          },
        ],
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockImplementation(
      (
        _workspace: string,
        _canvas: string,
        rowId?: string,
        alias?: string,
      ) => ({
        data: {
          record_id: rowId || null,
          record:
            rowId && alias === "other"
              ? { id: rowId, title: "Other record" }
              : null,
          can_edit: true,
        },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );

    renderCanvasApp();
    fireEvent.click(screen.getByText("Select first"));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent(
        "Other record",
      ),
    );
    expect(mockRuntimePrimaryRowId).toBeNull();
    expect(mockUseCanvasData).toHaveBeenCalledWith(
      "workspace-1",
      "canvas-1",
      "row-1",
      "other",
    );
    fireEvent.click(screen.getByText("Edit title"));
    fireEvent.click(screen.getByRole("button", { name: "Cancel" }));
    expect(screen.getByTestId("detail-title")).toHaveTextContent(
      "Other record",
    );
    fireEvent.click(screen.getByText("Edit title"));
    fireEvent.click(screen.getByRole("button", { name: "Save" }));
    await waitFor(() =>
      expect(submit).toHaveBeenCalledWith(
        "canvas-1",
        { data_source_alias: "other", data: { title: "Draft title" } },
        "row-1",
      ),
    );
  });

  it("returns to the source canvas after authored navigation", () => {
    render(
      <MemoryRouter
        initialEntries={[
          {
            pathname: "/workspace/workspace-1/myvault/canvases/canvas-1",
            state: {
              canvasReturnTo:
                "/workspace/workspace-1/myvault/canvases/source?row=own-row",
            },
          },
        ]}
      >
        <Routes>
          <Route
            path="/workspace/:workspaceId/myvault/canvases/:id"
            element={<CanvasApp />}
          />
          <Route
            path="/workspace/workspace-1/myvault/canvases/source"
            element={<span>Source canvas</span>}
          />
        </Routes>
      </MemoryRouter>,
    );
    fireEvent.click(screen.getByRole("button", { name: "Back" }));
    expect(screen.getByText("Source canvas")).toBeInTheDocument();
  });
});

describe("CanvasApp content width", () => {
  const widthOf = () =>
    screen.getByTestId("canvas-runtime-content") as HTMLElement;

  const renderWith = (extra: Record<string, unknown>) => {
    mockUseCanvas.mockReturnValue({
      canvas: {
        id: "canvas-1",
        name: "Wide",
        data_sources: [{ alias: "main", view_id: "view-1", is_primary: true }],
        elements: [
          {
            id: "field-1",
            element_type: "FIELD",
            data_binding: { data_source_alias: "main", field_path: "name" },
            config_json: { label: "Name" },
          },
        ],
        ...extra,
      },
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: {
        record_id: null,
        record: null,
        records: [],
        record_count: 0,
        can_edit: true,
        can_create: true,
      },
      loading: false,
      error: null,
      refresh: jest.fn(),
    });
    mockUseCanvasSubmit.mockReturnValue({
      submit: jest.fn(),
      loading: false,
      error: null,
      result: null,
    });
    renderCanvasApp();
  };

  it("stays at 900 px for a canvas with no layout setting", () => {
    renderWith({});
    expect(widthOf()).toHaveStyle("max-width: 900px");
  });

  it("drops the maximum width for layout_width full and keeps the padding", () => {
    renderWith({ theme_config: { layout_width: "full" } });
    expect(widthOf()).toHaveStyle("max-width: none");
    expect(widthOf()).toHaveStyle("padding: 24px");
  });

  it("uses a configured pixel width", () => {
    renderWith({ theme_config: { layout_width: 1300 } });
    expect(widthOf()).toHaveStyle("max-width: 1300px");
  });
});

describe("CanvasApp footer on a multi-tab canvas with a selection-bound card", () => {
  const twoTabCanvas = {
    id: "canvas-1",
    name: "Two tabs",
    data_sources: [{ alias: "main", view_id: "view-1", is_primary: true }],
    elements: [
      {
        id: "tabs",
        element_type: "CONTAINER",
        config_json: { container_type: "TABS" },
      },
      {
        id: "grid-1",
        element_type: "VIEW_GRID",
        parent_element_id: "tabs",
        parent_panel_id: "panel-a",
        config_json: { data_source_alias: "main" },
      },
      {
        id: "panel-1",
        element_type: "CONTAINER",
        parent_element_id: "tabs",
        parent_panel_id: "panel-a",
        config_json: {
          container_type: "SECTION",
          selection_binding: {
            source_grid_element_id: "grid-1",
            data_source_alias: "main",
            mode: "edit",
            empty_state_text: "Select a position",
          },
        },
      },
      {
        id: "field-a",
        element_type: "FIELD",
        parent_element_id: "panel-1",
        data_binding: { data_source_alias: "main", field_path: "title" },
        config_json: { label: "Title" },
      },
      {
        id: "field-b",
        element_type: "FIELD",
        parent_element_id: "tabs",
        parent_panel_id: "panel-b",
        data_binding: { data_source_alias: "main", field_path: "name" },
        config_json: { label: "Name" },
      },
    ],
  };

  beforeEach(() => {
    mockRendererMounts = 0;
    mockOnSelectionChange = undefined;
    mockUseCanvas.mockReturnValue({
      canvas: twoTabCanvas,
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string) => ({
        data: {
          record_id: rowId || null,
          record: rowId ? { id: rowId, title: "First" } : null,
          records: [],
          record_count: 0,
          can_edit: true,
          can_create: true,
        },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );
    mockUseCanvasSubmit.mockReturnValue({
      submit: jest.fn().mockResolvedValue({ success: true }),
      loading: false,
      error: null,
      result: null,
    });
  });

  it("shows the form footer on a tab that has nothing to do with the selection card", () => {
    renderCanvasApp();

    // Tab A holds the selection card and no row is selected: no footer.
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    expect(
      screen.queryByRole("button", { name: "Submit" }),
    ).not.toBeInTheDocument();

    // Tab B is an ordinary form: the footer is shown.
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    expect(screen.getByRole("button", { name: "Submit" })).toBeInTheDocument();

    // Back on tab A the card keeps its behaviour.
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    expect(
      screen.queryByRole("button", { name: "Submit" }),
    ).not.toBeInTheDocument();
  });

  it("keeps Save for the selected record on the card tab and Submit on the form tab", async () => {
    renderCanvasApp();

    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    expect(
      await screen.findByRole("button", { name: "Save" }),
    ).toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    expect(screen.getByRole("button", { name: "Submit" })).toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Save" }),
    ).not.toBeInTheDocument();
  });

  it("submits only the form tab's fields, as a new record, and never the selected row", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    renderCanvasApp();

    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    fireEvent.click(screen.getByRole("button", { name: "Edit name" }));
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));

    await waitFor(() => expect(submit).toHaveBeenCalledTimes(1));
    expect(submit).toHaveBeenCalledWith(
      "canvas-1",
      { data_source_alias: "main", data: { name: "Bob" } },
      undefined,
    );
  });
  it("does not carry the selected row's values into the other tab's form", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string) => ({
        data: {
          record_id: rowId || null,
          record: rowId ? { id: rowId, title: "First", name: "Sel" } : null,
          records: [],
          record_count: 0,
          can_edit: true,
          can_create: true,
        },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );
    renderCanvasApp();

    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    expect(await screen.findByTestId("detail-title")).toHaveTextContent(
      "First",
    );
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));

    // The Name field belongs to tab B: it starts empty, not with the row's name.
    expect(screen.getByTestId("name-value")).toHaveTextContent("");
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));
    await waitFor(() => expect(submit).toHaveBeenCalledTimes(1));
    expect(submit).toHaveBeenCalledWith(
      "canvas-1",
      { data_source_alias: "main", data: { name: undefined } },
      undefined,
    );

    // Back on the card tab the row's own fields are filled again.
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent("First"),
    );
  });

  const readOnlyTabBCanvas = {
    ...twoTabCanvas,
    elements: twoTabCanvas.elements.map((element) =>
      element.id === "field-b"
        ? {
            ...element,
            config_json: { ...element.config_json, displayMode: "text" },
          }
        : element,
    ),
  };

  it("retains the last primary values from the report tab through selection and personal refetch", async () => {
    mockUseCanvas.mockReturnValue({
      canvas: readOnlyTabBCanvas,
      loading: false,
      error: null,
    });
    const primary = {
      record_id: "me",
      record: { name: "Johan" },
      records: [],
      record_count: 1,
      can_edit: false,
      can_create: false,
    };
    let settle!: (value: unknown) => void;
    const pending = new Promise((resolve) => {
      settle = resolve;
    });
    let primaryCalls = 0;
    const authFetch = jest
      .fn()
      .mockImplementation((_url, _description, options) => {
        const request = JSON.parse(options.body);
        if (request.data_source_alias)
          return Promise.resolve({
            ...primary,
            record_id: request.record_id,
            record: request.record_id
              ? { title: "Selected review", name: "Other person" }
              : null,
          });
        primaryCalls += 1;
        return primaryCalls === 1 ? Promise.resolve(primary) : pending;
      });
    (useAuthFetch as jest.Mock).mockReturnValue(authFetch);
    const realHook = jest.requireActual("../hooks/useCanvases").useCanvasData;
    mockUseCanvasData.mockImplementation(realHook);
    renderCanvasApp("", queryClientWrapper);
    fireEvent.click(screen.getByRole("button", { name: "Show report tab" }));
    await waitFor(() => expect(primaryCalls).toBe(1));
    await waitFor(() => expect(authFetch).toHaveBeenCalledTimes(2));
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent(
        "Selected review",
      ),
    );
    expect(mockRuntimePrimaryRowId).toBe("me");
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() => expect(primaryCalls).toBe(2));
    expect(screen.getByTestId("name-value")).toHaveTextContent("Johan");
    expect(screen.getByTestId("name-value")).not.toHaveTextContent(
      "Other person",
    );
    await act(async () =>
      settle({ ...primary, record: { name: "Updated Johan" } }),
    );
    await waitFor(() =>
      expect(screen.getByTestId("name-value")).toHaveTextContent(
        "Updated Johan",
      ),
    );
  });

  it("shows the primary record on a read-only tab that is not the selection card", async () => {
    mockUseCanvas.mockReturnValue({
      canvas: readOnlyTabBCanvas,
      loading: false,
      error: null,
    });
    // The card tab reads the selected row; every other tab reads the primary record
    // (no row id, no alias), which is the signed-in user's own record on a personal tab.
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string, alias?: string) => {
        const record = rowId
          ? { id: rowId, title: "First", name: "Selected" }
          : { id: "me", name: "Primary" };
        return {
          data: {
            record_id: rowId || "me",
            record,
            records: [],
            record_count: 1,
            can_edit: true,
            can_create: true,
          },
          loadedFor: { rowId: rowId ?? null, alias: alias ?? null },
          loading: false,
          error: null,
          refresh: jest.fn(),
        };
      },
    );
    renderCanvasApp();

    // Nothing is selected, so tab A (the card tab) has no record, but tab B does.
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() =>
      expect(screen.getByTestId("name-value")).toHaveTextContent("Primary"),
    );

    // Selecting a row on tab A fills only the card; tab B keeps the primary record.
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    expect(await screen.findByTestId("detail-title")).toHaveTextContent(
      "First",
    );
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() =>
      expect(screen.getByTestId("name-value")).toHaveTextContent("Primary"),
    );
    expect(screen.getByTestId("name-value")).not.toHaveTextContent("Selected");
  });

  it("keeps a tab with a writable field an empty create form, never prefilled from the primary record", async () => {
    mockUseCanvasData.mockImplementation(
      (
        _workspace: string,
        _canvas: string,
        rowId?: string,
        alias?: string,
      ) => ({
        data: {
          record_id: rowId || "me",
          record: rowId
            ? { id: rowId, title: "First", name: "Selected" }
            : { id: "me", name: "Primary" },
          records: [],
          record_count: 1,
          can_edit: true,
          can_create: true,
        },
        loadedFor: { rowId: rowId ?? null, alias: alias ?? null },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );
    renderCanvasApp();
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    expect(screen.getByTestId("name-value")).toHaveTextContent("");
    expect(mockUseCanvasData).toHaveBeenLastCalledWith(
      expect.anything(),
      expect.anything(),
      undefined,
      undefined,
    );
  });

  it("offers no Submit on a read-only tab beside the card, even with a ?row= in the URL", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    mockUseCanvas.mockReturnValue({
      canvas: readOnlyTabBCanvas,
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockImplementation(
      (
        _workspace: string,
        _canvas: string,
        rowId?: string,
        alias?: string,
      ) => ({
        data: {
          record_id: rowId || "me",
          record: { id: rowId || "me", name: "Primary" },
          records: [],
          record_count: 1,
          can_edit: true,
          can_create: true,
        },
        loadedFor: { rowId: rowId ?? null, alias: alias ?? null },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );
    renderCanvasApp("?row=url-record");
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() =>
      expect(screen.getByTestId("name-value")).toHaveTextContent("Primary"),
    );
    expect(screen.queryByRole("button", { name: "Submit" })).toBeNull();
    expect(submit).not.toHaveBeenCalled();
  });

  it("keeps create semantics on a writable tab beside the card with a ?row= in the URL", async () => {
    const submit = jest.fn().mockResolvedValue({ success: true });
    mockUseCanvasSubmit.mockReturnValue({
      submit,
      loading: false,
      error: null,
      result: null,
    });
    renderCanvasApp("?row=url-record");
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    fireEvent.click(screen.getByRole("button", { name: "Submit" }));
    await waitFor(() => expect(submit).toHaveBeenCalledTimes(1));
    expect(submit.mock.calls[0][2]).toBeUndefined();
  });

  it("keeps a card's field state apart from a personal field on another tab that uses the same key", async () => {
    // Tab A's card has a "title" field and tab B has its own "title" field; both are bound to
    // the same field key but to different elements and data.
    const sharedKeyCanvas = {
      ...readOnlyTabBCanvas,
      elements: [
        ...readOnlyTabBCanvas.elements,
        {
          id: "field-b2",
          element_type: "FIELD",
          parent_element_id: "tabs",
          parent_panel_id: "panel-b",
          data_binding: { data_source_alias: "main", field_path: "title" },
          config_json: { label: "My title", displayMode: "text" },
        },
      ],
    };
    mockUseCanvas.mockReturnValue({
      canvas: sharedKeyCanvas,
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string, alias?: string) => {
        const record = rowId
          ? { id: rowId, title: rowId === "row-2" ? "Second" : "First" }
          : { id: "me", title: "Primary", name: "Primary" };
        return {
          data: {
            record_id: rowId || "me",
            record,
            records: [],
            record_count: 1,
            can_edit: true,
            can_create: true,
          },
          loadedFor: { rowId: rowId ?? null, alias: alias ?? null },
          loading: false,
          error: null,
          refresh: jest.fn(),
        };
      },
    );
    renderCanvasApp();

    // The personal tab shows its own value before any selection.
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() =>
      expect(screen.getByTestId("tab-b-title")).toHaveTextContent("Primary"),
    );

    // Select a row on the card tab: the card fills, the personal tab keeps its value.
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    expect(await screen.findByTestId("detail-title")).toHaveTextContent(
      "First",
    );
    expect(screen.getByTestId("tab-b-title")).toHaveTextContent("Primary");

    // An edit in the card does not reach the personal field.
    fireEvent.click(screen.getByRole("button", { name: "Edit title" }));
    expect(screen.getByTestId("detail-title")).toHaveTextContent("Draft title");
    expect(screen.getByTestId("tab-b-title")).toHaveTextContent("Primary");

    // Leaving the card tab clears only the card; the personal field is not blanked.
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() =>
      expect(screen.getByTestId("tab-b-title")).toHaveTextContent("Primary"),
    );
    expect(screen.getByTestId("detail-title")).toHaveTextContent("");

    // Back on the card tab the selected row is shown again, and another row replaces it.
    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent("First"),
    );
    fireEvent.click(screen.getByRole("button", { name: "Select second" }));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent("Second"),
    );
    expect(screen.getByTestId("tab-b-title")).toHaveTextContent("Primary");

    // Clearing the selection empties the card and leaves the personal field alone.
    fireEvent.click(screen.getByRole("button", { name: "Clear selection" }));
    await waitFor(() =>
      expect(screen.getByTestId("detail-title")).toHaveTextContent(""),
    );
    expect(screen.getByTestId("tab-b-title")).toHaveTextContent("Primary");
  });

  it("does not show the card's record on another tab while that tab's own record is loading", async () => {
    mockUseCanvas.mockReturnValue({
      canvas: readOnlyTabBCanvas,
      loading: false,
      error: null,
    });
    // The hook keeps the previous data until the refetch lands; it says which query
    // that data belongs to.
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string, alias?: string) => {
        const stale = !rowId && !alias;
        return {
          data: {
            record_id: "sel-1",
            record: { id: "sel-1", title: "First", name: "Selected" },
            records: [],
            record_count: 1,
            can_edit: true,
            can_create: true,
          },
          loadedFor: stale ? { rowId: "sel-1", alias: "main" } : null,
          loading: stale,
          error: null,
          refresh: jest.fn(),
        };
      },
    );
    renderCanvasApp();
    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    expect(screen.getByTestId("name-value")).toHaveTextContent("");
  });

  it("gives action buttons the whole selected record on the card tab and none of it elsewhere", async () => {
    // Actions template {{field}} on the server from this row data, and the card
    // shows only the title.
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string) => ({
        data: {
          record_id: rowId || null,
          record: rowId
            ? { id: rowId, title: "First", owner_email: "boss@example.test" }
            : null,
          records: [],
          record_count: 0,
          can_edit: true,
          can_create: true,
        },
        loading: false,
        error: null,
        refresh: jest.fn(),
      }),
    );
    renderCanvasApp();

    fireEvent.click(screen.getByRole("button", { name: "Select first" }));
    await waitFor(() =>
      expect(mockRuntimeRowData).toMatchObject({
        id: "row-1",
        title: "First",
        owner_email: "boss@example.test",
      }),
    );

    // A card edit still wins over the record.
    fireEvent.click(screen.getByRole("button", { name: "Edit title" }));
    await waitFor(() =>
      expect(mockRuntimeRowData).toMatchObject({
        title: "Draft title",
        owner_email: "boss@example.test",
      }),
    );

    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await waitFor(() =>
      expect(mockRuntimeRowData).not.toHaveProperty("owner_email"),
    );
  });

  it("keeps the shown tab and the canvas mounted while the data refetches on a tab switch", async () => {
    // Real hook semantics: loading flips true when rowId or the alias change and
    // the previous data stays until the response arrives.
    mockUseCanvasData.mockImplementation(
      (_workspace: string, _canvas: string, rowId?: string, alias?: string) => {
        const [loading, setLoading] = React.useState(true);
        React.useEffect(() => {
          setLoading(true);
          const timer = setTimeout(() => setLoading(false), 10);
          return () => clearTimeout(timer);
        }, [rowId, alias]);
        return {
          data: {
            record_id: rowId || null,
            record: rowId ? { id: rowId, title: "First" } : null,
            records: [],
            record_count: 0,
            can_edit: true,
            can_create: true,
          },
          loading,
          error: null,
          refresh: jest.fn(),
        };
      },
    );
    renderCanvasApp();
    expect(await screen.findByTestId("canvas-renderer")).toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    await new Promise((resolve) => setTimeout(resolve, 60));

    expect(screen.getByTestId("shown-tab")).toHaveTextContent("panel-b");
    expect(screen.getByRole("button", { name: "Submit" })).toBeInTheDocument();
    expect(mockRendererMounts).toBe(1);
  });
});

describe("CanvasApp footer on a tabbed canvas with a read-only analytics tab", () => {
  const analyticsAndFormCanvas = {
    id: "canvas-1",
    name: "Analytics and form",
    data_sources: [{ alias: "main", view_id: "view-1", is_primary: true }],
    elements: [
      {
        id: "tabs",
        element_type: "CONTAINER",
        config_json: { container_type: "TABS" },
      },
      {
        id: "kpi-a",
        element_type: "WIDGET",
        parent_element_id: "tabs",
        parent_panel_id: "panel-a",
        config_json: {
          widget_type: "METRIC",
          data_source_alias: "main",
          metric_config: { aggregation: "sum", value_field: "employees" },
        },
      },
      {
        id: "field-b",
        element_type: "FIELD",
        parent_element_id: "tabs",
        parent_panel_id: "panel-b",
        data_binding: { data_source_alias: "main", field_path: "name" },
        config_json: { label: "Name" },
      },
    ],
  };

  beforeEach(() => {
    mockUseCanvas.mockReturnValue({
      canvas: analyticsAndFormCanvas,
      loading: false,
      error: null,
    });
    mockUseCanvasData.mockReturnValue({
      data: {
        record_id: null,
        record: null,
        records: [],
        record_count: 0,
        can_edit: true,
        can_create: true,
      },
      loading: false,
      error: null,
      refresh: jest.fn(),
    });
    mockUseCanvasSubmit.mockReturnValue({
      submit: jest.fn().mockResolvedValue({ success: true }),
      loading: false,
      error: null,
      result: null,
    });
  });

  it("shows no Cancel/Submit footer on a tab that has only widgets, and shows it on the tab with a writable field", () => {
    renderCanvasApp();

    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    expect(
      screen.queryByRole("button", { name: "Submit" }),
    ).not.toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Cancel" }),
    ).not.toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Show tab B" }));
    expect(screen.getByRole("button", { name: "Submit" })).toBeInTheDocument();
    expect(screen.getByRole("button", { name: "Cancel" })).toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Show tab A" }));
    expect(
      screen.queryByRole("button", { name: "Submit" }),
    ).not.toBeInTheDocument();
  });
});
